import {
  Avatar,
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close,
  HomeOutlined,
  KeyboardDoubleArrowLeft,
  KeyboardDoubleArrowRight,
  UnfoldMore,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { routes } from '../../routes/app';
import { IRoute } from '../../interfaces';
import Detail from '../../pages/Detail/Detail';
import { useReva } from '../../contexts/RevaContext';
import useReviewQueueCount from '../../hooks/useReviewQueueCount';
import useAdminQueueCounts from '../../hooks/useAdminQueueCounts';
import { MenuListItem, railItemSx } from './MenuListItem';
import {
  buildNavGroups,
  initialsFor,
  requiresExactMatch,
  roleLabelFor,
} from './navModel';

interface NavRailProps {
  /** Labels and group overlines are shown; false is the 72px icon rail. */
  expanded: boolean;
  /** Rendered inside the phone sheet: full labels, a close button, no collapse row. */
  mobile: boolean;
  onToggleExpanded: () => void;
  onClose: () => void;
  onOpenAccountMenu: (anchor: HTMLElement) => void;
  /** Route keys held back for a customer with nothing on that side yet. */
  hiddenKeys?: ReadonlySet<string>;
}

const PBUYER_HOME: IRoute = {
  key: 'pbuyer-home-route',
  title: 'Home',
  path: '/pbuyer',
  enabled: true,
  component: Detail,
  icon: HomeOutlined,
  menu: true,
  protected: true,
};

const GroupLabel: React.FC<{ label?: string; expanded: boolean }> = ({
  label,
  expanded,
}) => {
  if (!label) return null;
  if (!expanded) {
    return (
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', mx: 1.25, my: 1 }} />
    );
  }
  return (
    <Typography
      component="div"
      sx={{
        fontSize: '0.66rem',
        fontWeight: 700,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        color: 'rgba(255,255,255,0.42)',
        px: 1.5,
        pt: 1.5,
        pb: 0.5,
      }}
    >
      {label}
    </Typography>
  );
};

/**
 * The navy brand rail: wordmark, grouped nav, the Ask Reva tile and the
 * account block. One component serves the pinned desktop rail, the collapsed
 * icon rail and the phone sheet.
 */
const NavRail: React.FC<NavRailProps> = ({
  expanded,
  mobile,
  onToggleExpanded,
  onClose,
  onOpenAccountMenu,
  hiddenKeys,
}) => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const userType = currentUser?.type;
  const isAdmin = userType === 'ADMIN';
  const showLabels = expanded || mobile;

  const reviewQueueCount = useReviewQueueCount(isAdmin);
  const { openEscalations, pendingApprovals } = useAdminQueueCounts(isAdmin);
  const counts: Record<string, { count: number; hot?: boolean }> = {
    'admin-dashboard': { count: pendingApprovals },
    'escalations-admin': { count: openEscalations, hot: true },
    'review-queue-admin': { count: reviewQueueCount },
  };

  const {
    openReva,
    available: revaAvailable,
    unseenCount: revaUnseenCount,
  } = useReva();

  const groups = buildNavGroups(
    routes,
    userType,
    userType === 'PBUYER' ? [PBUYER_HOME] : [],
    hiddenKeys
  );

  const allItems = groups.flatMap((group) => group.items);

  const fullName =
    [currentUser?.firstName, currentUser?.lastName].filter(Boolean).join(' ') ||
    currentUser?.email ||
    'Account';

  const revaTile = revaAvailable && (
    <Tooltip
      title={showLabels ? '' : 'Ask Reva'}
      placement="right"
      disableHoverListener={showLabels}
      disableFocusListener={showLabels}
    >
      <ListItemButton
        aria-label="Ask Reva"
        onClick={() => {
          onClose();
          openReva();
        }}
        sx={{
          ...railItemSx,
          mt: 0.75,
          minHeight: showLabels ? 52 : 44,
          backgroundColor: 'rgba(39,154,241,0.22)',
          color: '#fff',
          justifyContent: showLabels ? 'flex-start' : 'center',
          px: showLabels ? 1.5 : 0,
          width: showLabels ? 'auto' : 48,
          mx: showLabels ? 0 : 'auto',
          '&:hover': { backgroundColor: 'rgba(39,154,241,0.32)' },
        }}
      >
        <ListItemIcon
          sx={{ minWidth: 0, mr: showLabels ? 1.5 : 0, justifyContent: 'center' }}
        >
          <Badge
            data-testid="reva-toolbar-badge"
            badgeContent={revaUnseenCount > 0 ? revaUnseenCount : undefined}
            overlap="circular"
            invisible={revaUnseenCount === 0}
            sx={{
              '& .MuiBadge-badge': {
                color: 'black',
                backgroundColor: 'highlight.main',
                fontWeight: 700,
                // With labels the count sits in the row instead. Hidden with
                // CSS rather than `invisible`, which would freeze the badge's
                // text at its last visible value.
                display: showLabels ? 'none' : undefined,
              },
            }}
          >
            <img
              src="/reva-icon-white.png"
              alt=""
              style={{ width: 24, height: 24, display: 'block' }}
            />
          </Badge>
        </ListItemIcon>
        {showLabels && (
          <>
            <ListItemText
              primary="Ask Reva"
              secondary={
                revaUnseenCount > 0
                  ? `${revaUnseenCount} new ${revaUnseenCount === 1 ? 'reply' : 'replies'}`
                  : isAdmin
                    ? 'Your deal assistant'
                    : 'Your home assistant'
              }
              primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }}
              secondaryTypographyProps={{
                fontSize: '0.74rem',
                color: 'rgba(255,255,255,0.7)',
              }}
              sx={{ my: 0 }}
            />
            {revaUnseenCount > 0 && (
              <Box
                component="span"
                data-testid="reva-nav-count"
                sx={{
                  ml: 'auto',
                  minWidth: 20,
                  height: 20,
                  px: 0.75,
                  borderRadius: 10,
                  backgroundColor: 'primary.main',
                  color: '#fff',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                {revaUnseenCount}
              </Box>
            )}
          </>
        )}
      </ListItemButton>
    </Tooltip>
  );

  return (
    <Box
      data-testid="app-nav"
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'secondary.main',
        color: '#fff',
      }}
    >
      {/* Brand row */}
      <Box
        sx={{
          height: 64,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: showLabels ? 'space-between' : 'center',
          px: showLabels ? 2 : 0,
        }}
      >
        {showLabels ? (
          <img src="/homepoint_logo_white.png" alt="Homepoint" style={{ height: 30 }} />
        ) : (
          <img src="/square_logo_white.png" alt="Homepoint" style={{ height: 28 }} />
        )}
        {mobile && (
          <IconButton
            aria-label="Close menu"
            onClick={onClose}
            sx={{ color: 'rgba(255,255,255,0.8)', mr: -1 }}
          >
            <Close />
          </IconButton>
        )}
      </Box>

      {/* Nav groups */}
      <List
        component="nav"
        aria-label="Main"
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', px: 1.5, py: 0.5 }}
      >
        {groups.map((group, index) => (
          <Box key={group.label ?? `group-${index}`} sx={{ display: 'grid', gap: 0.25 }}>
            <GroupLabel label={group.label} expanded={showLabels} />
            {group.items.map((route) => (
              <MenuListItem
                key={route.key}
                route={route}
                expanded={showLabels}
                exact={requiresExactMatch(route, allItems)}
                badgeCount={counts[route.key]?.count ?? 0}
                hot={counts[route.key]?.hot}
              />
            ))}
            {index === 0 && revaTile}
          </Box>
        ))}
      </List>

      {/* Account + collapse */}
      <Box
        sx={{
          flex: 'none',
          borderTop: '1px solid rgba(255,255,255,0.1)',
          px: 1.5,
          pt: 1,
          pb: mobile ? 2 : 1,
        }}
      >
        <ListItemButton
          data-testid="nav-account"
          aria-label="Account menu"
          aria-haspopup="menu"
          onClick={(event) => onOpenAccountMenu(event.currentTarget)}
          sx={{
            ...railItemSx,
            minHeight: 48,
            justifyContent: showLabels ? 'flex-start' : 'center',
            px: showLabels ? 1 : 0,
            width: showLabels ? 'auto' : 48,
            mx: showLabels ? 0 : 'auto',
          }}
        >
          <Avatar
            sx={{
              width: 34,
              height: 34,
              bgcolor: 'primary.main',
              fontSize: '0.8rem',
              fontWeight: 700,
              mr: showLabels ? 1.25 : 0,
            }}
          >
            {initialsFor(currentUser ?? undefined)}
          </Avatar>
          {showLabels && (
            <>
              <ListItemText
                primary={fullName}
                secondary={roleLabelFor(userType)}
                primaryTypographyProps={{
                  noWrap: true,
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  color: '#fff',
                }}
                secondaryTypographyProps={{
                  noWrap: true,
                  fontSize: '0.74rem',
                  color: 'rgba(255,255,255,0.6)',
                }}
                sx={{ my: 0, minWidth: 0 }}
              />
              <UnfoldMore sx={{ fontSize: 18, color: 'rgba(255,255,255,0.6)', ml: 1 }} />
            </>
          )}
        </ListItemButton>

        {!mobile && (
          <ListItemButton
            aria-label={expanded ? 'Collapse navigation' : 'Expand navigation'}
            data-testid="nav-toggle"
            onClick={onToggleExpanded}
            sx={{
              ...railItemSx,
              minHeight: 36,
              mt: 0.5,
              color: 'rgba(255,255,255,0.5)',
              justifyContent: expanded ? 'flex-start' : 'center',
              px: expanded ? 1.5 : 0,
              width: expanded ? 'auto' : 48,
              mx: expanded ? 0 : 'auto',
            }}
          >
            {expanded ? (
              <KeyboardDoubleArrowLeft sx={{ fontSize: 20 }} />
            ) : (
              <KeyboardDoubleArrowRight sx={{ fontSize: 20 }} />
            )}
            {expanded && (
              <Typography sx={{ ml: 1.5, fontSize: '0.78rem', fontWeight: 600 }}>
                Collapse
              </Typography>
            )}
          </ListItemButton>
        )}
      </Box>
    </Box>
  );
};

export default NavRail;
